import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanFreeText } from "@domain/project/floorplan/floorplan-free-text";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanImage } from "@domain/project/floorplan/floorplan-image";
import { FloorplanNode } from "@domain/project/floorplan/floorplan-node";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanRemoteSensor } from "@domain/project/floorplan/floorplan-remote-sensor";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanText } from "@domain/project/floorplan/floorplan-text";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import {
  FREE_TEXT_MAX_FONT_SIZE,
  FREE_TEXT_MAX_TEXT_BOX_HEIGHT,
  FREE_TEXT_MAX_TEXT_BOX_WIDTH,
  FREE_TEXT_MIN_FONT_SIZE,
  FREE_TEXT_MIN_TEXT_BOX_HEIGHT,
  FREE_TEXT_MIN_TEXT_BOX_WIDTH,
} from "@lib/constants/floorplan-free-text";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { FreeTextKonva } from "@project/floorplanner/konva/free-text-konva";
import Konva from "konva";
import { Circle } from "konva/lib/shapes/Circle";
import { Transformer } from "konva/lib/shapes/Transformer";
import { Wedge } from "konva/lib/shapes/Wedge";

interface FreeTextTransformState {
  fontSize: number;
  originalTextBoxWidth: number;
  originalTextBoxHeight: number;
  currentTextBoxWidth: number;
  currentTextBoxHeight: number;
  itemTextScaleX: number;
  itemTextScaleY: number;
  lastScaleX: number;
  lastScaleY: number;
  anchor?: string | null;
}

export class TransformerKonva {
  private nodeCenterValues: Map<
    Konva.Node,
    {
      centerX: number;
      centerY: number;
    }
  > = new Map();
  private freeTextTransformStates = new Map<Konva.Node, FreeTextTransformState>();

  private transformer = new Transformer({
    rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],
    rotationSnapTolerance: 8,
    anchorSize: 15,
    rotateEnabled: true,
    flipEnabled: false,
    keepRatio: false,
    boundBoxFunc: (oldBox, newBox) => {
      const node = this.transformer.nodes()[0]; // get the shape currently being transformed
      if (!node) return newBox;

      const minWidth = node.getAttr("minWidth") ?? 50;
      const minHeight = node.getAttr("minHeight") ?? 50;

      const width = newBox.width;
      const height = newBox.height;

      if (width < minWidth || height < minHeight) {
        return oldBox;
      }
      return newBox;
    },
  });
  private circleTransformer = new Transformer({
    anchorSize: 15,
    rotateEnabled: false,
    flipEnabled: false,
    keepRatio: true,
    centeredScaling: true,
  });
  private imageTransformer = new Transformer({
    anchorSize: 15,
    enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"],
    rotateEnabled: false,
    flipEnabled: false,
    keepRatio: true,
    boundBoxFunc: (oldBox, newBox) => {
      const minSize = 20;
      return newBox.width < minSize && newBox.height < minSize ? oldBox : newBox;
    },
  });
  private iconTransformer = new Transformer({
    anchorSize: 15,
    rotateEnabled: false,
    flipEnabled: false,
    keepRatio: true,
    enabledAnchors: ["top-right", "top-left", "bottom-right", "bottom-left"],
    centeredScaling: true,
  });
  private freeTextTransformer = new Transformer({
    anchorSize: 15,
    rotateEnabled: false,
    flipEnabled: false,
    keepRatio: true,
    enabledAnchors: [
      "top-left",
      "top-center",
      "top-right",
      "middle-left",
      "middle-right",
      "bottom-left",
      "bottom-center",
      "bottom-right",
    ],
    boundBoxFunc: (oldBox, newBox) => {
      if (newBox.width < FREE_TEXT_MIN_TEXT_BOX_WIDTH || newBox.height < FREE_TEXT_MIN_TEXT_BOX_HEIGHT) {
        return oldBox;
      }

      return newBox;
    },
  });
  private coneTransformer = new Transformer({
    anchorSize: 15,
    rotateEnabled: true,
    resizeEnabled: false,
    flipEnabled: false,
    rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],
    rotationSnapTolerance: 8,
  });
  private lockedTransformer = new Transformer({
    resizeEnabled: false,
    rotateEnabled: false,
    flipEnabled: false,
  });
  private multiSelectTransformer = new Transformer({
    rotateEnabled: false,
    flipEnabled: false,
    keepRatio: true,
    centeredScaling: true,
    enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"],
  });

  get transformers(): Transformer[] {
    return [
      this.transformer,
      this.circleTransformer,
      this.coneTransformer,
      this.imageTransformer,
      this.iconTransformer,
      this.freeTextTransformer,
      this.lockedTransformer,
      this.multiSelectTransformer,
    ];
  }

  get isMultiSelectActive(): boolean {
    return this.multiSelectTransformer.nodes().length > 0;
  }

  reset(shape: Konva.Node): void {
    this.clear();
    this.attach(shape);
  }

  resetIfSingleSelect(shape: Konva.Node): void {
    if (!this.isMultiSelectActive) {
      this.reset(shape);
    }
  }

  add(shape: Konva.Node): void {
    const transformer = this.select(shape);
    transformer.nodes([...transformer.nodes(), shape]);
    transformer.moveToTop();
  }

  addToMultiSelect(shape: Konva.Node): void {
    this.multiSelectTransformer.nodes([...this.multiSelectTransformer.nodes(), shape]);
    this.multiSelectTransformer.moveToTop();
  }

  remove(shape: Konva.Node): void {
    for (const transformer of this.transformers) {
      const nodes = transformer.nodes();
      const idx = nodes.indexOf(shape);
      if (idx !== -1) {
        transformer.nodes(nodes.filter((n) => n !== shape));
        break;
      }
    }
  }

  clear(): void {
    this.transformer.nodes([]);
    this.circleTransformer.nodes([]);
    this.coneTransformer.nodes([]);
    this.iconTransformer.nodes([]);
    this.lockedTransformer.nodes([]);
    this.imageTransformer.nodes([]);
    this.freeTextTransformer.nodes([]);
    this.multiSelectTransformer.nodes([]);
  }

  private attach(shape: Konva.Node): void {
    const transformer = this.select(shape);
    transformer.nodes([shape]);
    transformer.moveToTop();
  }

  private select(shape: Konva.Node): Transformer {
    const floorplanItem = shape.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;

    if (floorplanItem.locked) {
      return this.lockedTransformer;
    }

    if (shape instanceof Wedge) {
      if (floorplanItem instanceof FloorplanFlameDetector || floorplanItem instanceof FloorplanFlameDetectorPlaceholder) {
        return this.coneTransformer;
      }
      return this.lockedTransformer;
    } else if (shape instanceof Circle) {
      if (floorplanItem instanceof FloorplanTransmitter || floorplanItem instanceof FloorplanTransmitterPlaceholder) {
        if (!floorplanItem.isMonitoringAreaScaleAble()) {
          return this.lockedTransformer;
        }
      }
      return this.circleTransformer;
    } else if (floorplanItem instanceof FloorplanImage) {
      return this.imageTransformer;
    } else if (floorplanItem instanceof FloorplanFreeText) {
      return this.freeTextTransformer;
    } else if (
      floorplanItem instanceof FloorplanProductItem ||
      floorplanItem instanceof FloorplanText ||
      floorplanItem instanceof FloorplanTransmitterPlaceholder ||
      floorplanItem instanceof FloorplanSimplePlaceholder ||
      floorplanItem instanceof FloorplanRemoteSensor ||
      floorplanItem instanceof FloorplanGasWarningCenterPlaceholder ||
      floorplanItem instanceof FloorplanFlameDetectorPlaceholder
    ) {
      return this.iconTransformer;
    } else {
      return this.transformer;
    }
  }

  constructor() {
    this.freeTextTransformer.on("transformstart", () => {
      const node = this.freeTextTransformer.nodes()[0];
      const freeText = node?.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanFreeText | undefined;
      const group = node as Konva.Group;
      const text = group?.findOne(".freeTextValue") as Konva.Text | null;
      if (!node || !(freeText instanceof FloorplanFreeText) || !text) {
        return;
      }

      this.freeTextTransformStates.set(node, {
        fontSize: freeText.fontSize,
        originalTextBoxWidth: text.width(),
        originalTextBoxHeight: text.height(),
        currentTextBoxWidth: text.width(),
        currentTextBoxHeight: text.height(),
        itemTextScaleX: (node.getParent()?.findOne(".itemText") as Konva.Group | null)?.scaleX() ?? 1,
        itemTextScaleY: (node.getParent()?.findOne(".itemText") as Konva.Group | null)?.scaleY() ?? 1,
        lastScaleX: node.scaleX(),
        lastScaleY: node.scaleY(),
        anchor: this.freeTextTransformer.getActiveAnchor(),
      });
      const activeAnchor = this.freeTextTransformer.getActiveAnchor();
      group.setAttr("freeTextTransformActive", true);
      const isCornerAnchor = this.isCornerAnchor(activeAnchor);
      this.freeTextTransformer.keepRatio(isCornerAnchor);
      this.freeTextTransformer.centeredScaling(isCornerAnchor);
    });

    this.freeTextTransformer.on("transform", () => {
      const node = this.freeTextTransformer.nodes()[0];
      const state = node ? this.freeTextTransformStates.get(node) : undefined;
      if (!state || !node) {
        return;
      }

      state.anchor = this.freeTextTransformer.getActiveAnchor();
      state.lastScaleX = node.scaleX();
      state.lastScaleY = node.scaleY();

      const isHorizontalSideAnchor = this.isHorizontalSideAnchor(state.anchor);
      const isVerticalSideAnchor = this.isVerticalSideAnchor(state.anchor);
      const isSideAnchor = isHorizontalSideAnchor || isVerticalSideAnchor;
      const group = node as Konva.Group;
      const parentGroup = group.getParent() as Konva.Group | null;
      const freeText = node.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanFreeText | undefined;

      if (isHorizontalSideAnchor) {
        state.currentTextBoxWidth = Math.min(
          FREE_TEXT_MAX_TEXT_BOX_WIDTH,
          Math.max(FREE_TEXT_MIN_TEXT_BOX_WIDTH, Math.round(state.originalTextBoxWidth * Math.abs(state.lastScaleX))),
        );
      }

      if (isVerticalSideAnchor) {
        state.currentTextBoxHeight = Math.min(
          FREE_TEXT_MAX_TEXT_BOX_HEIGHT,
          Math.max(FREE_TEXT_MIN_TEXT_BOX_HEIGHT, Math.round(state.originalTextBoxHeight * Math.abs(state.lastScaleY))),
        );
      }

      if (isSideAnchor) {
        const text = group.findOne(".freeTextValue") as Konva.Text | null;
        if (text && state.lastScaleX !== 0 && state.lastScaleY !== 0) {
          // Keep the text glyphs at their original size in both transform directions.
          text.scale({ x: 1 / state.lastScaleX, y: 1 / state.lastScaleY });
        }

        if (parentGroup && freeText instanceof FloorplanFreeText) {
          FreeTextKonva.applyItemTextScale(freeText, parentGroup, state.itemTextScaleX, state.itemTextScaleY);
        }
      } else if (this.isCornerAnchor(state.anchor) && parentGroup && freeText instanceof FloorplanFreeText) {
        const scale = Math.min(Math.abs(state.lastScaleX), Math.abs(state.lastScaleY));
        FreeTextKonva.applyItemTextScale(freeText, parentGroup, state.itemTextScaleX * scale, state.itemTextScaleY * scale);
      }
    });

    this.freeTextTransformer.on("transformend", () => {
      const node = this.freeTextTransformer.nodes()[0];
      const state = node ? this.freeTextTransformStates.get(node) : undefined;
      const freeText = node?.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanFreeText | undefined;
      const parentGroup = node?.getParent() as Konva.Group | null;
      if (!node || !state || !(freeText instanceof FloorplanFreeText) || !parentGroup) {
        return;
      }

      const isHorizontalSideAnchor = this.isHorizontalSideAnchor(state.anchor);
      const isVerticalSideAnchor = this.isVerticalSideAnchor(state.anchor);
      const isSideAnchor = isHorizontalSideAnchor || isVerticalSideAnchor;
      const scaleX = Math.abs(state.lastScaleX);
      const scaleY = Math.abs(state.lastScaleY);
      const scale = isSideAnchor ? scaleX : Math.min(scaleX, scaleY);

      let textBoxWidth = freeText.textBoxWidth;
      if (isHorizontalSideAnchor) {
        textBoxWidth = Math.min(FREE_TEXT_MAX_TEXT_BOX_WIDTH, Math.max(FREE_TEXT_MIN_TEXT_BOX_WIDTH, state.currentTextBoxWidth));
      } else if (!isVerticalSideAnchor) {
        textBoxWidth = Math.min(
          FREE_TEXT_MAX_TEXT_BOX_WIDTH,
          Math.max(FREE_TEXT_MIN_TEXT_BOX_WIDTH, Math.round(state.originalTextBoxWidth * scale)),
        );
      }

      let textBoxHeight = freeText.textBoxHeight;
      if (isVerticalSideAnchor) {
        textBoxHeight = Math.min(
          FREE_TEXT_MAX_TEXT_BOX_HEIGHT,
          Math.max(FREE_TEXT_MIN_TEXT_BOX_HEIGHT, state.currentTextBoxHeight),
        );
      } else if (!isSideAnchor) {
        textBoxHeight = Math.min(
          FREE_TEXT_MAX_TEXT_BOX_HEIGHT,
          Math.max(FREE_TEXT_MIN_TEXT_BOX_HEIGHT, Math.round(state.originalTextBoxHeight * scale)),
        );
      }
      const fontSize = Math.min(FREE_TEXT_MAX_FONT_SIZE, Math.max(FREE_TEXT_MIN_FONT_SIZE, Math.round(state.fontSize * scale)));
      const group = node as Konva.Group;
      const text = group.findOne(".freeTextValue") as Konva.Text | null;
      text?.scale({ x: 1, y: 1 });
      node.scale({ x: 1, y: 1 });
      group.setAttr("freeTextTransformActive", false);
      this.freeTextTransformer.keepRatio(true);
      this.freeTextTransformer.centeredScaling(false);
      freeText.textBoxWidth = textBoxWidth;
      freeText.textBoxHeight = textBoxHeight;
      if (!isSideAnchor) {
        freeText.fontSize = fontSize;
      }
      FreeTextKonva.refreshText(freeText, parentGroup);
      FreeTextKonva.repositionItemText(freeText, parentGroup);
      this.freeTextTransformStates.delete(node);
    });
  }

  private isCornerAnchor(anchor: string | null | undefined): boolean {
    return anchor === "top-left" || anchor === "top-right" || anchor === "bottom-left" || anchor === "bottom-right";
  }

  private isHorizontalSideAnchor(anchor: string | null | undefined): boolean {
    return anchor === "middle-left" || anchor === "middle-right";
  }

  private isVerticalSideAnchor(anchor: string | null | undefined): boolean {
    return anchor === "top-center" || anchor === "bottom-center";
  }

  initMultiSelectBehavior(): void {
    this.multiSelectTransformer.on("transformstart", () => {
      this.nodeCenterValues = new Map();

      this.multiSelectTransformer.nodes().forEach((node) => {
        const originalX = node.x();
        const originalY = node.y();
        const originalScaleX = node.scaleX();
        const originalScaleY = node.scaleY();

        const halfWidth = (node.width() * originalScaleX) / 2;
        const halfHeight = (node.height() * originalScaleY) / 2;

        this.nodeCenterValues.set(node, {
          centerX: originalX + halfWidth,
          centerY: originalY + halfHeight,
        });
      });
    });

    this.multiSelectTransformer.on("transform", () => {
      this.multiSelectTransformer.nodes().forEach((node) => {
        const originalCenter = this.nodeCenterValues.get(node);
        if (!originalCenter) return;

        const correctedScale = Math.max(node.scaleX(), node.scaleY());
        node.scaleX(correctedScale);
        node.scaleY(correctedScale);

        const newHalfWidth = (node.width() * node.scaleX()) / 2;
        const newHalfHeight = (node.height() * node.scaleY()) / 2;

        node.x(originalCenter.centerX - newHalfWidth);
        node.y(originalCenter.centerY - newHalfHeight);
      });
    });

    this.multiSelectTransformer.on("transformend", () => {
      this.multiSelectTransformer.scaleX(1);
      this.multiSelectTransformer.scaleY(1);
    });
  }
}
